/** @format */

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {Box, Button, InputAdornment, Menu, Tab, Typography} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';

import {AccountsKpiName} from 'graphql/generated/types';

import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';
import LegacyTextField from 'components/inputs/LegacyTextField';

import {
  AnalyticGoalFromQuery,
  KPIConfig,
  KpiLocalSettings,
  KpiSettingsFromQuery,
  TokensFromQuery,
  YalperAnalyticAttributionModelEnum,
} from 'pages/Home/Project/Steps/Periods/interfaces';
import {formatKpiValue, parseFormattedNumber} from 'pages/Home/Project/Steps/Periods/utils';
import {toStr} from 'helpers/stringHelpers';

import {STATS_PROVIDERS} from 'pages/HomeGrouping/const';

import AnalyticGoals from './components/AnalyticGoals';
import {ATTRIBUTION_MODELS, clickPaymentKPIs, impressionPaymentKPIs} from './constants';
import {
  ErrorBorder,
  StyledKpiPlanForm,
  StyledMenuItem,
  StyledTabs,
  Switch,
  classes,
} from './styles';

interface KpiPlanFormProps {
  selectedKpis: KpiLocalSettings[];
  primaryKpiId: string | null;
  mediaplanType?: 'clicks' | 'impressions';
  analyticGoals?: ReadonlyArray<AnalyticGoalFromQuery>;
  kpiSettings?: KpiSettingsFromQuery;
  selectedGoals: string[];
  onSelectedGoalsChange: (goals: string[]) => void;
  onKpisChange: (kpis: KpiLocalSettings[]) => void;
  onPrimaryKpiChange: (id: string) => void;
  isLoading?: boolean;
  error?: string | null;
  tokens: TokensFromQuery;
}

const KpiPlanForm = ({
  selectedKpis,
  primaryKpiId,
  mediaplanType = 'clicks',
  analyticGoals = [],
  kpiSettings,
  selectedGoals,
  onSelectedGoalsChange,
  onKpisChange,
  onPrimaryKpiChange,
  isLoading = false,
  error = null,
  tokens,
}: KpiPlanFormProps) => {
  const {t} = useTranslation();

  const [kpiMenuAnchorEl, setKpiMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [activeKpiId, setActiveKpiId] = useState<string | null>(primaryKpiId);

  const [includeLeadFormsStats, setIncludeLeadFormsStats] = useState<boolean>(true);

  const goalTableData = useMemo(() => {
    return analyticGoals.map(goal => ({
      id: toStr(goal.id),
      name: goal.name,
      counterId: toStr(goal.counterId),
      counterName: goal.counterName,
    }));
  }, [analyticGoals]);

  const availableKPIs = useMemo(
    () => (mediaplanType === 'clicks' ? clickPaymentKPIs : impressionPaymentKPIs),
    [mediaplanType],
  );

  const initializedRef = useRef(false);
  const goalsInitializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;

    initializedRef.current = true;

    if (selectedKpis.length === 0) {
      const defaultConfig = availableKPIs.find(kpi => kpi.id === 'CPL') ?? availableKPIs[0];
      if (!defaultConfig) return;
      const defaultKpi: KpiLocalSettings = {
        id: defaultConfig.id,
        name: defaultConfig.name as AccountsKpiName,
        description: defaultConfig.description,
        value: null,
        needsMetricGoals: defaultConfig.needsMetricGoals,
        primary: true,
      };
      onKpisChange([defaultKpi]);
      onPrimaryKpiChange(defaultKpi.id);
      setActiveKpiId(defaultKpi.id);
    } else if (!activeKpiId && selectedKpis.length > 0) {
      setActiveKpiId(selectedKpis[0].id);
    }
  }, [selectedKpis, activeKpiId, availableKPIs, onKpisChange, onPrimaryKpiChange]);

  useEffect(() => {
    if (
      !goalsInitializedRef.current &&
      kpiSettings &&
      kpiSettings.goals.length > 0 &&
      selectedGoals.length === 0
    ) {
      const goalIds = kpiSettings.goals.map(goalId => String(goalId));

      if (goalIds.length > 0) {
        onSelectedGoalsChange(goalIds);
        goalsInitializedRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kpiSettings, onSelectedGoalsChange]);

  const handleOpenKpiMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setKpiMenuAnchorEl(event.currentTarget);
  }, []);

  const handleCloseKpiMenu = useCallback(() => {
    setKpiMenuAnchorEl(null);
  }, []);

  const handleAddKpi = useCallback(
    (kpiConfig: KPIConfig) => {
      const newKpi: KpiLocalSettings = {
        id: kpiConfig.id,
        name: kpiConfig.name as AccountsKpiName,
        description: kpiConfig.description,
        value: null,
        needsMetricGoals: kpiConfig.needsMetricGoals,
        primary: selectedKpis.length === 0,
      };

      const updatedKpis = [...selectedKpis, newKpi];

      if (updatedKpis.length === 1 || !primaryKpiId) {
        onPrimaryKpiChange(newKpi.id);
      }

      onKpisChange(updatedKpis);
      setActiveKpiId(newKpi.id);
      handleCloseKpiMenu();
    },
    [selectedKpis, primaryKpiId, onKpisChange, onPrimaryKpiChange, handleCloseKpiMenu],
  );

  const handleRemoveKpi = useCallback(
    (id: string) => {
      const updatedKpis = selectedKpis.filter(kpi => kpi.id !== id);

      if (id === primaryKpiId && updatedKpis.length > 0) {
        onPrimaryKpiChange(updatedKpis[0].id);
      }

      if (id === activeKpiId && updatedKpis.length > 0) {
        setActiveKpiId(updatedKpis[0].id);
      } else if (updatedKpis.length === 0) {
        setActiveKpiId(null);
      }

      onKpisChange(updatedKpis);
    },
    [selectedKpis, primaryKpiId, activeKpiId, onKpisChange, onPrimaryKpiChange],
  );

  const handleSetPrimaryKpi = useCallback(
    (id: string) => {
      onPrimaryKpiChange(id);

      const updatedKpis = selectedKpis.map(kpi => ({
        ...kpi,
        primary: kpi.id === id,
      }));

      onKpisChange(updatedKpis);
    },
    [selectedKpis, onKpisChange, onPrimaryKpiChange],
  );

  const handleKpiValueChange = useCallback(
    (id: string, value: string) => {
      const numericValue = parseFormattedNumber(value);

      const updatedKpis = selectedKpis.map(kpi =>
        kpi.id === id ? {...kpi, value: numericValue} : kpi,
      );

      onKpisChange(updatedKpis);
    },
    [selectedKpis, onKpisChange],
  );

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setActiveKpiId(newValue);
  }, []);

  const handleGoalRemove = useCallback(
    (id: string) => {
      onSelectedGoalsChange(selectedGoals.filter(g => g !== id));
    },
    [selectedGoals, onSelectedGoalsChange],
  );

  const handleLeadFormsStatsToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setIncludeLeadFormsStats(event.target.checked);
  }, []);

  const isVkProject = useMemo(() => {
    const adTokens = tokens.filter(token => !STATS_PROVIDERS.includes(token.provider));

    const adToken = adTokens[0];
    if (!adToken) return false;

    return adToken.provider === 'VKONTAKTE' || adToken.provider === 'VKONTAKTE_ADS';
  }, [tokens]);

  const activeKpi = useMemo(() => {
    return selectedKpis.find(kpi => kpi.id === activeKpiId);
  }, [selectedKpis, activeKpiId]);

  const mapKpiTab = (kpi: KpiLocalSettings) => {
    const kpiItem = availableKPIs.find(item => item.id === kpi.id);
    return (
      <Tab
        key={kpi.id}
        value={kpi.id}
        label={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box>
              {kpi.id === primaryKpiId && <Box className={classes.kpiMainIndicatorTab} />}
              {kpiItem ? kpiItem.name : kpi.name}
            </Box>
            <CloseIcon
              className={classes.kpiRemoveIconTab}
              onClick={e => {
                e.stopPropagation();
                handleRemoveKpi(kpi.id);
              }}
            />
          </Box>
        }
      />
    );
  };

  return (
    <StyledKpiPlanForm>
      <Typography className={classes.kpiHeading}>
        <Trans>План KPI на период</Trans>
      </Typography>

      <Box sx={{mt: 4}}>
        <Typography className={classes.kpiDescription}>
          <Trans>
            Задайте планы нужных KPI, с которыми будут сравниваться фактические значения
          </Trans>
        </Typography>
        <Typography className={classes.kpiDescription}>
          <Trans>Для показателей без заданных планов будет выводиться только факт</Trans>
        </Typography>
      </Box>

      {selectedKpis.length > 0 && (
        <Box className={classes.kpiTabsWrapper}>
          <StyledTabs
            value={activeKpiId}
            onChange={handleTabChange}
            variant='scrollable'
            scrollButtons='auto'
          >
            {selectedKpis.map(mapKpiTab)}
          </StyledTabs>

          <Button className={classes.addKpiButtonSmall} onClick={handleOpenKpiMenu}>
            <AddIcon className={classes.addKpiButtonIcon} />
            <Trans>Добавить KPI</Trans>
          </Button>
        </Box>
      )}

      {activeKpi && (
        <ErrorBorder hasError={activeKpi.value === null}>
          <Box className={classes.kpiItemBox}>
            <Box className={classes.kpiItemContent}>
              {activeKpi.primary && (
                <PrimaryMediumButton className={classes.kpiMainLabel}>
                  <Box>
                    <Typography className={classes.setMainButtonText}>
                      <Trans>Основной</Trans>
                    </Typography>
                  </Box>
                </PrimaryMediumButton>
              )}
              {!activeKpi.primary && (
                <PrimaryMediumButton
                  className={classes.setMainButton}
                  onClick={() => handleSetPrimaryKpi(activeKpi.id)}
                >
                  <Typography className={classes.setMainButtonText}>
                    <Trans>Сделать основным</Trans>
                  </Typography>
                </PrimaryMediumButton>
              )}
            </Box>
          </Box>

          {activeKpi.needsMetricGoals ? (
            <Box className={classes.kpiGoalsContainer}>
              {selectedGoals.length > 0 && (
                <Typography className={classes.kpiGoalsTitle}>
                  <Trans>Выбранные цели</Trans>
                </Typography>
              )}

              <Box className={classes.kpiGoalsList}>
                {selectedGoals.map(goalId => {
                  const goalName = analyticGoals.find(g => toStr(g.id) === goalId)?.name ?? goalId;
                  return (
                    <Box key={goalId} className={classes.kpiGoalItem}>
                      <Typography className={classes.kpiGoalText}>{goalName}</Typography>
                      <CloseIcon
                        className={classes.kpiGoalRemoveIcon}
                        onClick={() => handleGoalRemove(goalId)}
                      />
                    </Box>
                  );
                })}
              </Box>

              <AnalyticGoals
                data={goalTableData}
                selectedGoalIds={selectedGoals}
                onGoalsConfirm={onSelectedGoalsChange}
                isError={Boolean(error)}
                isLoading={isLoading}
              />

              <LegacyTextField
                className={classes.kpiValueInput}
                type='text'
                value={formatKpiValue(activeKpi.value)}
                placeholder={t('Плановое значение', 'Плановое значение')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleKpiValueChange(activeKpi.id, e.target.value)
                }
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position='end'>
                        <Typography sx={{color: '#fff'}}>
                          {activeKpi.id === 'CPL' ? '₽' : ''}
                        </Typography>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              {activeKpi.value === null && (
                <Typography className={classes.kpiErrorText}>
                  <Trans>Необходимо указать плановое значение</Trans>
                </Typography>
              )}

              <Box sx={{mt: 6, mb: 3}}>
                <Typography sx={{fontSize: '14px', fontWeight: 500}}>
                  <Trans>Модель атрибуции</Trans>
                  {': '}
                  {ATTRIBUTION_MODELS.find(
                    m =>
                      m.value ===
                      (kpiSettings?.attributionModel ??
                        YalperAnalyticAttributionModelEnum.AUTOMATIC),
                  )?.label ??
                    kpiSettings?.attributionModel ??
                    YalperAnalyticAttributionModelEnum.AUTOMATIC}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: theme => theme.palette.semantic_dark?.surface.onSurfaceMuted,
                    mt: 1,
                  }}
                >
                  <Trans>Настраивается в разделе Настройка проекта</Trans>
                </Typography>
              </Box>

              {isVkProject && (
                <Box className={classes.switchWrapper}>
                  <Typography className={classes.switchDescription}>
                    <Trans>Учитывать статистику лид-форм</Trans>
                  </Typography>
                  <Box sx={{ml: 4}}>
                    <Switch
                      checked={includeLeadFormsStats}
                      onChange={handleLeadFormsStatsToggle}
                      inputProps={{
                        'aria-label': 'Учитывать статистику лид-форм',
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <LegacyTextField
              className={classes.kpiValueInput}
              type='text'
              value={formatKpiValue(activeKpi.value)}
              placeholder={t('Плановое значение', 'Плановое значение')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleKpiValueChange(activeKpi.id, e.target.value)
              }
            />
          )}
        </ErrorBorder>
      )}

      {selectedKpis.length === 0 && (
        <Button
          variant='outlined'
          startIcon={<AddIcon />}
          className={classes.addKpiButton}
          onClick={handleOpenKpiMenu}
        >
          <Trans>Добавить KPI</Trans>
        </Button>
      )}

      <Menu anchorEl={kpiMenuAnchorEl} open={Boolean(kpiMenuAnchorEl)} onClose={handleCloseKpiMenu}>
        {(() => {
          const filteredKpis = availableKPIs.filter(kpi => {
            return !selectedKpis.some(
              selected =>
                selected.id === kpi.id ||
                selected.id.includes(`_${kpi.id}`) ||
                selected.name === kpi.name,
            );
          });

          return filteredKpis.map(kpi => (
            <StyledMenuItem key={kpi.id} onClick={() => handleAddKpi(kpi)}>
              <Typography>{kpi.name}</Typography>
              {kpi.description && (
                <Typography variant='body2' sx={{fontSize: 12}}>
                  {kpi.description}
                </Typography>
              )}
            </StyledMenuItem>
          ));
        })()}
      </Menu>
    </StyledKpiPlanForm>
  );
};

export default KpiPlanForm;
